import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  fadeInOutAnimation,
  quickFadeInOutAnimation,
  slideInOutAnimation,
} from '@app/core/animations';
import { AuthState } from '@app/core/auth/states/auth.state';
import { SetCurrentApp } from '@app/core/states/app.actions';
import { AppState } from '@app/core/states/app.state';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { ResponsiveState } from '@app/core/states/responsive.state';
import { OpenNotificationForm } from '@app/features/notifications/states/notifications.actions';
import { NotificationsState } from '@app/features/notifications/states/notifications.state';
import { Select, Store } from '@ngxs/store';
import { filter, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'rk-notification-shell',
  templateUrl: './notification-shell.component.html',
  styleUrls: ['./notification-shell.component.scss'],
  animations: [fadeInOutAnimation, slideInOutAnimation, quickFadeInOutAnimation],
  standalone: false,
})
export class NotificationShellComponent implements OnInit, OnDestroy {
  @Select(NotificationsState.isFormVisible)
  isFormVisible$: Observable<boolean>;
  @Select(NotificationsState.isOpenFormButtonVisible)
  isOpenFormButtonVisible$: Observable<boolean>;
  @Select(AppState.isLoading)
  isLoading$: Observable<boolean>;
  @Select(NotificationsState.totalNumber)
  totalNumber$: Observable<number>;
  @Select(AppState.hasNotificationsEnabled)
  hasNotificationsEnabled$: Observable<boolean>;
  @Select(ResponsiveState.showAppManagerMobileWarning)
  showAppManagerMobileWarning$: Observable<boolean>;
  @Select(AuthState.isAdminOrSuperuser)
  isAdminOrSuperuser$: Observable<boolean>;
  @Select(AppState.currentAppId)
  currentAppId$: Observable<number>;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  openForm(event: string) {
    this.store.dispatch(
      new SetEventProperties({
        name: event,
        component: 'Button',
      }),
    );
    this.store.dispatch(new LogEvent('Button Clicked'));
    this.store.dispatch(new OpenNotificationForm());
  }

  ngOnInit() {
    this.subscriptions.add(
      this.currentAppId$.subscribe(appId => {
        if (appId && !this.router.url.includes(`/notifications/${appId}`)) {
          this.router.navigateByUrl(`/notifications/${appId}`);
        }
      }),
    );

    // get route parameters
    this.subscriptions.add(
      this.route?.params.pipe(filter(params => !!params)).subscribe(params => {
        const appId = params['appId'];
        if (appId) {
          this.store.dispatch(new SetCurrentApp(appId));
        }
      }),
    );

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'PageNotifications',
        component: 'Page',
      }),
    );
    this.store.dispatch(new LogEvent('Page Viewed'));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
